import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { useLanguage } from 'shared/context/LanguageContext';

interface StepTransitionProps {
  stepKey: number;
  children: React.ReactNode;
}

const StepTransition: React.FC<StepTransitionProps> = ({ stepKey, children }) => {
  const { dir } = useLanguage();
  const reduceMotion = useReducedMotion();

  const prevKeyRef = useRef(stepKey);
  const directionRef = useRef<1 | -1>(1);

  useEffect(() => {
    if (stepKey !== prevKeyRef.current) {
      directionRef.current = stepKey > prevKeyRef.current ? 1 : -1;
      prevKeyRef.current = stepKey;
    }
  }, [stepKey]);

  const baseDelta = 48;
  const axisSign = dir === 'rtl' ? -1 : 1;
  const enterX = reduceMotion ? 0 : baseDelta * directionRef.current * axisSign;
  const exitX = reduceMotion ? 0 : -baseDelta * directionRef.current * axisSign;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={stepKey}
        initial={{ opacity: 0, x: enterX, y: reduceMotion ? 0 : 8 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        exit={{ opacity: 0, x: exitX, y: reduceMotion ? 0 : -4 }}
        transition={{
          duration: reduceMotion ? 0 : 0.42,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="will-change-transform"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export default StepTransition;
